import React, {useState, useEffect, useContext} from 'react';
import {useRouter} from 'next/router';
import _ from 'lodash';
import {
  ChatAlt2Icon,
  UsersIcon,
  AcademicCapIcon,
} from '@heroicons/react/outline';

import CoachCard from '../components/CoachCard';
import CoachesProvider, {CoachesContext} from '../components/CoachesProvider';
import DocumentHeader from '../components/DocumentHeader';
import Button, {ButtonTypes} from '../components/ButtonNew';

import {startsWithVowel} from '../util';
import {useAnalytics} from '../hooks';

const SkillsLandingComponent = ({tag, skill}) => {
  const {
    coaches,
    companies: companyLogos,
  } = useContext(CoachesContext);
  const router = useRouter();

  const {faq, slug} = skill;
  const skillName = tag.name;
  const [isLoading, setIsLoading] = useState(true);
  const [filteredCoaches, setFilteredCoaches] = useState([]);
  const {tracker} = useAnalytics();

  const pageTitle = `Find ${startsWithVowel(skillName) ? 'an' : 'a'} ${skillName} coach and mentor`;
  const pageSubtitle = `Level up your ${skillName} skills with help from our team of experts from FAANG+ tech companies`;

  useEffect(() => {
    tracker.skillLandingView({skill: skillName});
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (coaches) {
      const filtered = _.filter(coaches, (coach) => _.some(coach?.Skills, {name: skillName}));
      setFilteredCoaches(filtered);
      setIsLoading(false);
    }
  }, [coaches, skillName]);

  const onClickCoach = (coach) => (e) => {
    e.stopPropagation();
    tracker.skillClickCoach({skill: skillName, coach: coach.slug});
    router.push(`/coach/${coach.slug}`);
  };

  return (
    <div className="relative bg-white overflow-hidden">
      <DocumentHeader
        title={pageTitle}
        socialDescription={pageSubtitle}
      />
      <div className="pt-6 pb-8 md:pt-12 md:pb-12 flex flex-col md:flex-row justify-center items-center text-center">
        <div className="w-full max-w-4xl p-4">
          {/* Title elements */}
          <span className="inline-flex mb-2 uppercase tracking-wider text-indigo-600 mx-auto font-semibold">Coaching</span>
          <h1 className="text-2xl md:text-5xl text-center text-black font-black leading-none">
            {pageTitle}
          </h1>
          <h3 className="mx-auto mt-3 mb-6 text-base md:text-xl text-center text-gray-500 font-medium leading-tight max-w-xl">
            {pageSubtitle}
          </h3>
        </div>
      </div>

      {/* Services description */}
      <div className="flex flex-col md:flex-row w-full max-w-6xl mx-auto mb-4">
        <div className="p-4 md:w-1/3">
          <div className="flex flex-col justify-center">
            <div className="flex items-center justify-start">
              <ChatAlt2Icon className="w-5 h-5 mr-2 text-indigo" />
              <h3 className="text-base font-semibold">{`${skillName} interview prep`}</h3>
            </div>
            <p className="mt-2 text-gray-500 font-medium">
              {`Find ${startsWithVowel(skillName) ? 'an' : 'a'} ${skillName} mentor who can help with mock interviews, applying to jobs, and more.`}
            </p>
          </div>
        </div>
        <div className="p-4 md:w-1/3">
          <div className="flex flex-col justify-center">
            <div className="flex items-center justify-start">
              <UsersIcon className="w-5 h-5 mr-2 text-indigo" />
              <h3 className="text-base font-semibold">Career coaching</h3>
            </div>
            <p className="mt-2 text-gray-500 font-medium">
              {`Find a coach with ${skillName} expertise who can help you navigate career options, switch jobs/industries, and more.`}
            </p>
          </div>
        </div>
        <div className="p-4 md:w-1/3">
          <div className="flex flex-col justify-center">
            <div className="flex items-center justify-start">
              <AcademicCapIcon className="w-5 h-5 mr-2 text-indigo" />
              <h3 className="text-base font-semibold">Mentorship</h3>
            </div>
            <p className="mt-2 text-gray-500 font-medium">{`Level up your ${skillName} with a coach who can teach you more about ${skillName}`}</p>
          </div>
        </div>
      </div>

      {/* Display top coaches */}
      <div className="flex flex-col w-full max-w-6xl mx-auto p-4">
        <div className="mb-5">
          <h2 className="text-lg md:text-2xl text-center md:text-left text-black font-black leading-none">
            {`Top ${skillName} coaches`}
          </h2>
        </div>
        {filteredCoaches.length > 0 ? (
          <>
            <div className="flex flex-col md:flex-row justify-center gap-6">
              {filteredCoaches.slice(0, 3).map((coach) => (
                <div key={coach.slug} className="w-full md:w-1/3">
                  <CoachCard
                    name={coach.Name}
                    title={coach.Title}
                    image={coach.Photo?.[0].url}
                    href={`/coach/${coach.slug}`}
                    companies={coach.Companies}
                    companyLogos={companyLogos}
                    skills={coach.Skills}
                    services={coach.Services}
                    rating={coach.Rating}
                    description={coach['Brief Bio']}
                    numSessions={coach['Total number of sessions completed']}
                    onClick={onClickCoach(coach)}
                  />
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="h-56 border-4 border-gray-100 border-dashed rounded-lg flex items-center justify-center">
            <p className="text-base text-gray-500 font-semibold">
              {isLoading ? 'Fetching coaches...' : 'Sorry! No coaches available at this time.'}
            </p>
          </div>
        )}
        <div className="flex items-center justify-center mt-5">
          {filteredCoaches.length > 3 ? (
            <Button
              buttonType={ButtonTypes.TERTIARY}
              href={`/coaching?skill=${slug}`}
              className="font-semibold"
              onClick={() => tracker.skillClickMoreCoach({skill: skillName})}
            >
              {`Browse more ${skillName} coaches ->`}
            </Button>
          ) : (
            <Button
              buttonType={ButtonTypes.TERTIARY}
              href="/coaching"
              className="font-semibold"
              onClick={() => tracker.skillClickMoreCoach({skill: skillName})}
            >
              {'Browse all coaches ->'}
            </Button>
          )}
        </div>
      </div>

      {/* About */}
      <div className="flex flex-col w-full max-w-6xl mx-auto p-4 mb-16">
        <div className="mb-5">
          <h2 className="text-lg md:text-2xl text-center md:text-left text-black font-black leading-none">
            {`About ${skillName}`}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {faq && (
            faq.map((item) => (
              <div className="flex flex-col justify-start">
                <div className="text-base font-semibold">{item.question}</div>
                <p className="mt-2 text-gray-500 font-medium">{item.answer}</p>
              </div>
            ))
          )}
        </div>
      </div>

    </div>
  );
};

const SkillsLandingPage = (props) => (
  <CoachesProvider>
    <SkillsLandingComponent {...props} />
  </CoachesProvider>
);

export default SkillsLandingPage;
